/**
 * AI模型配置编辑器
 * 支持LLM模型配置、提示词、系统提示等
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Typography,
  Switch,
  Divider,
  Alert,
} from 'antd'
import {
  RobotOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'

const { TextArea } = Input
const { Text } = Typography

// 模型提供商选项
const PROVIDER_OPTIONS = [
  { value: 'doubao', label: '豆包 (Doubao)', color: 'blue' },
  { value: 'openai', label: 'OpenAI', color: 'green' },
  { value: 'anthropic', label: 'Anthropic', color: 'orange' },
  { value: 'local', label: '本地模型', color: 'purple' },
  { value: 'custom', label: '自定义', color: 'default' },
]

// 能力选项
const CAPABILITY_OPTIONS = [
  { value: 'conversation', label: '对话生成' },
  { value: 'reasoning', label: '逻辑推理' },
  { value: 'creativity', label: '创意写作' },
  { value: 'analysis', label: '数据分析' },
  { value: 'code', label: '代码生成' },
  { value: 'translation', label: '翻译' },
]

interface AiModelEditorFormProps {
  initialData?: Record<string, any>
  onSave: (data: Record<string, any>) => Promise<void>
  loading?: boolean
}

const AiModelEditorForm: React.FC<AiModelEditorFormProps> = ({
  initialData,
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm()
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)
  const [capabilities, setCapabilities] = useState<string[]>([])
  const [fallbackModels, setFallbackModels] = useState<string[]>([])

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        provider: initialData.provider,
        model_id: initialData.model_id,
        temperature: initialData.temperature,
        max_tokens: initialData.max_tokens,
        system_prompt: initialData.system_prompt,
      })
      // 初始化 Select mode="tags" 字段
      setCapabilities(initialData.capabilities || [])
      setFallbackModels(initialData.fallback_models || [])
    } else {
      form.resetFields()
      setCapabilities([])
      setFallbackModels([])
    }
  }, [initialData, form])

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const modelData = {
        key: values.key,
        name: values.name,
        provider: values.provider,
        model_id: values.model_id,
        temperature: values.temperature,
        max_tokens: values.max_tokens,
        system_prompt: values.system_prompt,
        capabilities: capabilities,
        fallback_models: fallbackModels,
      }

      await onSave(modelData)
    } catch (error) {
      console.error('表单验证失败:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={
        <Space>
          <RobotOutlined style={{ color: '#722ed1' }} />
          <span>{initialData?.name || '新建AI模型'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
          {initialData?.provider && (
            <Tag color={PROVIDER_OPTIONS.find((o) => o.value === initialData?.provider)?.color}>
              {PROVIDER_OPTIONS.find((o) => o.value === initialData?.provider)?.label}
            </Tag>
          )}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="模型键" rules={[{ required: true }]}>
                  <Input placeholder="例如: model_doubao_pro" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="模型名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 豆包Pro版" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="provider" label="提供商" rules={[{ required: true }]}>
                  <Select placeholder="选择提供商" options={PROVIDER_OPTIONS} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="model_id" label="模型ID" rules={[{ required: true }]}>
              <Input placeholder="例如: doubao-seed-evolving" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="description" label="描述">
                  <TextArea rows={2} placeholder="模型的用途说明..." />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="能力">
                  <Select
                    mode="tags"
                    value={capabilities}
                    onChange={setCapabilities}
                    placeholder="conversation, reasoning"
                    style={{ width: '100%' }}
                  >
                    {CAPABILITY_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="生成参数" key="parameters">
            <Alert
              message="生成参数说明"
              description="控制模型输出的质量和风格。温度越高，输出越随机；最大令牌数限制单次回复长度。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="temperature" label="温度 (0-2)">
                  <InputNumber
                    min={0}
                    max={2}
                    step={0.1}
                    style={{ width: '100%' }}
                    placeholder="0.7"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="max_tokens" label="最大令牌数">
                  <InputNumber
                    min={100}
                    max={128000}
                    style={{ width: '100%' }}
                    placeholder="4096"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="top_p" label="Top P">
                  <InputNumber
                    min={0}
                    max={1}
                    step={0.1}
                    style={{ width: '100%' }}
                    placeholder="0.9"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="frequency_penalty" label="频率惩罚">
                  <InputNumber
                    min={-2}
                    max={2}
                    step={0.1}
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="presence_penalty" label="存在惩罚">
                  <InputNumber
                    min={-2}
                    max={2}
                    step={0.1}
                    style={{ width: '100%' }}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="logit_bias" label="Logit偏差">
                  <Input style={{ width: '100%' }} placeholder="{}" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="seed" label="随机种子">
                  <InputNumber style={{ width: '100%' }} placeholder="null" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="系统提示词" key="system_prompt">
            <Alert
              message="系统提示词说明"
              description="系统提示词定义AI角色的基本行为、个性和约束。它会在每次对话开始时发送给模型。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="system_prompt" label="系统提示词">
              <TextArea
                rows={12}
                placeholder={`你是一个沉浸式剧本游戏中的NPC，名叫[NPC名字]。

## 基本信息
- 年龄：30-35岁
- 身份：神秘男子
- 性格：沉默寡言，眼神中透着坚毅

## 说话风格
- 说话简洁，每句话都有深意
- 偶尔会流露出怀念的表情
- 不会主动透露过多信息

## 背景故事
[详细的背景故事...]

## 行为约束
- 只回答与游戏相关的问题
- 不会破坏游戏剧情
- 保持角色一致性

## 示例对话
Q: 你好，请问你是谁？
A: (沉默片刻) ...一个过路人。

Q: 这里最近有什么奇怪的事吗？
A: (眼神闪烁) ...注意观察就好。`}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
            </Form.Item>

            <Divider />

            <Form.Item name="user_prompt_template" label="用户提示模板">
              <TextArea
                rows={6}
                placeholder={`## 对话历史
{dialogue_history}

## 当前上下文
{context}

## 玩家问题
{player_input}

请根据以上信息，生成符合角色性格的回复。`}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
          </TabPane>

          <TabPane tab="备用模型" key="fallback">
            <Alert
              message="备用模型配置"
              description="当主模型不可用或响应超时时，自动切换到备用模型。"
              type="info"
              style={{ marginBottom: 16 }}
            />
            <Form.Item label="备用模型列表">
              <Select
                mode="tags"
                value={fallbackModels}
                onChange={setFallbackModels}
                placeholder="model_openai_gpt4, model_anthropic_claude"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Divider />

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name={['fallback', 'timeout']} label="超时时间 (ms)">
                  <InputNumber
                    min={1000}
                    style={{ width: '100%' }}
                    placeholder="30000"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['fallback', 'max_retries']} label="最大重试次数">
                  <InputNumber
                    min={0}
                    max={5}
                    style={{ width: '100%' }}
                    placeholder="2"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name={['fallback', 'circuit_breaker']} label="熔断启用" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="使用场景" key="usage">
            <Row gutter={16}>
              <Col span={8}>
                <Card size="small" title="NPC对话">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>需要自然语言生成</li>
                    <li>角色扮演一致性</li>
                    <li>上下文记忆</li>
                  </ul>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="剧情生成">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>分支剧情发展</li>
                    <li>对话内容生成</li>
                    <li>线索描述</li>
                  </ul>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="智能判断">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>条件评估</li>
                    <li>技能检定</li>
                    <li>玩家意图理解</li>
                  </ul>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Card size="small" title="推荐配置">
              <pre style={{ margin: 0, fontSize: 12 }}>
{`# NPC对话场景
provider: "doubao"
model_id: "doubao-seed-evolving"
temperature: 0.7
max_tokens: 2048

# 剧情生成场景
provider: "doubao"
model_id: "doubao-seed-evolving"
temperature: 0.9
max_tokens: 4096

# 智能判断场景
provider: "openai"
model_id: "gpt-4"
temperature: 0.3
max_tokens: 1024`}
              </pre>
            </Card>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

const { TabPane } = Tabs
const { Option } = Select

export default AiModelEditorForm
